import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (schoolId: string) => void;
}

const PLAN_TIERS = [
  { value: 'free', label: 'Free' },
  { value: 'individual', label: 'Individual' },
  { value: 'school', label: 'School' },
  { value: 'trust', label: 'Trust' },
];

const PLAN_SUB_TIERS = [
  { value: 'none', label: 'None' },
  { value: 'starter', label: 'Starter' },
  { value: 'standard', label: 'Standard' },
  { value: 'plus', label: 'Plus' },
];

const BILLING_STATUSES = [
  { value: 'trialing', label: 'Trialing (30-day trial)' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

export default function AdminCreateAccountDialog({ open, onOpenChange, onCreated }: Props) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [planTier, setPlanTier] = useState('school');
  const [planSubTier, setPlanSubTier] = useState('none');
  const [billingStatus, setBillingStatus] = useState('trialing');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!fullName.trim()) e.fullName = 'Full name is required';
    if (!email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) e.email = 'Valid email is required';
    if (!schoolName.trim()) e.schoolName = 'School name is required';
    if (!planTier) e.planTier = 'Plan is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-create-account', {
        body: {
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          schoolName: schoolName.trim(),
          planTier,
          planSubTier: planSubTier === 'none' ? null : planSubTier,
          billingStatus,
          sendEmail,
        },
      });
      if (error) throw new Error((data as { error?: string } | null)?.error ?? error.message);
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Account created',
        description: `${schoolName.trim()} has been set up.${sendEmail ? ' A welcome email with a password-setup link has been sent.' : ''}`,
      });

      onOpenChange(false);
      resetForm();

      if (onCreated && data?.schoolId) {
        onCreated(data.schoolId);
      } else if (data?.schoolId) {
        navigate(`/admin/schools/${data.schoolId}`);
      }
    } catch (err: unknown) {
      toast({
        title: 'Failed to create account',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFullName('');
    setEmail('');
    setSchoolName('');
    setPlanTier('school');
    setPlanSubTier('none');
    setBillingStatus('trialing');
    setSendEmail(true);
    setErrors({});
  };

  const handleOpenChange = (v: boolean) => {
    if (!submitting) {
      if (!v) resetForm();
      onOpenChange(v);
    }
  };

  const showSubTier = planTier === 'school' || planTier === 'trust';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create new account</DialogTitle>
          <DialogDescription>
            Provision a school and admin user directly. A welcome email with a password-setup link
            will be sent to the user.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Admin user details */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Account holder</p>
            <div className="space-y-1.5">
              <Label htmlFor="ac-fullname">Full name</Label>
              <Input
                id="ac-fullname"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Jane Smith"
                autoFocus
              />
              {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ac-email">Email address</Label>
              <Input
                id="ac-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="jane@school.org.uk"
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>
          </div>

          {/* School details */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">School</p>
            <div className="space-y-1.5">
              <Label htmlFor="ac-school">School name</Label>
              <Input
                id="ac-school"
                value={schoolName}
                onChange={e => setSchoolName(e.target.value)}
                placeholder="Maple Primary School"
              />
              {errors.schoolName && <p className="text-xs text-destructive">{errors.schoolName}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Plan tier</Label>
                <Select value={planTier} onValueChange={setPlanTier}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_TIERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.planTier && <p className="text-xs text-destructive">{errors.planTier}</p>}
              </div>

              {showSubTier && (
                <div className="space-y-1.5">
                  <Label>Sub-tier</Label>
                  <Select value={planSubTier} onValueChange={setPlanSubTier}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAN_SUB_TIERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Billing status</Label>
              <Select value={billingStatus} onValueChange={setBillingStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Email option */}
          <div className="flex items-start gap-3 pt-1">
            <Checkbox
              id="ac-send-email"
              checked={sendEmail}
              onCheckedChange={v => setSendEmail(!!v)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="ac-send-email" className="font-normal cursor-pointer">
                Send welcome email with password-setup link
              </Label>
              <p className="text-xs text-muted-foreground">
                Includes 2FA setup instructions. Link is valid for 24 hours.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>
              : 'Create account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
